import React, { createContext, useContext, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
}

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface AuthState {
  user: AuthUser | null;
  session: AuthSession | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export interface AuthError {
  message: string;
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
}

// Demo-mode mock — forge-auth (@blueforge-studio/service-auth) is consumed by @clinic-os/api
// The SPA stores the bearer token and forwards it via Authorization header on /api/* calls.
const MOCK_USER: AuthUser = {
  id: 'demo-user-id',
  email: 'demo@clinic.os',
  name: 'Demo User',
};

const MOCK_SESSION: AuthSession = {
  accessToken: 'demo-token',
  refreshToken: 'demo-refresh-token',
  expiresAt: Math.floor(Date.now() / 1000) + 3600,
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth-state'],
    queryFn: async () => {
      return { user: MOCK_USER, session: MOCK_SESSION };
    },
    staleTime: Infinity,
    refetchOnWindowFocus: true,
  });

  const signIn = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      queryClient.setQueryData(['auth-state'], { user: MOCK_USER, session: MOCK_SESSION });
      return { error: null };
    },
  });

  const signUp = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      queryClient.setQueryData(['auth-state'], { user: MOCK_USER, session: MOCK_SESSION });
      return { error: null };
    },
  });

  const signOut = useCallback(async () => {
    queryClient.setQueryData(['auth-state'], { user: null, session: null });
  }, [queryClient]);

  const resetPassword = useMutation({
    mutationFn: async (_email: string) => {
      return { error: null };
    },
  });

  const value: AuthContextValue = {
    user: data?.user ?? null,
    session: data?.session ?? null,
    isLoading,
    isAuthenticated: !!(data?.user && data?.session),
    signIn: async (email: string, password: string) => {
      return signIn.mutateAsync({ email, password });
    },
    signUp: async (email: string, password: string) => {
      return signUp.mutateAsync({ email, password });
    },
    signOut,
    resetPassword: async (email: string) => {
      return resetPassword.mutateAsync(email);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}